import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { Plus, Download, Trash2, Upload, FileText } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import "../styles/TrainingMatrix.css";

const TrainingMatrix = () => {
  const { t, i18n } = useTranslation();
  const [matrixData, setMatrixData] = useState([]);
  const [status, setStatus] = useState("");
  const [selectedCertificateType, setSelectedCertificateType] = useState("");
  const [editingCell, setEditingCell] = useState(null); // { rowId, field }
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    employeeName: "",
    trainingType: "",
    dateOfIssue: "",
    examDate: "",
    validityPeriod: "1",
    nextTrainingDate: "",
    remarks: "",
  });

  // Certificate types matching CreateCertificate
  const certificateTypes = [
    "Namensschild",
    "Verschraubungsmonteur",
    "Flanschmonteur",
    "PSAgA",
    "Jährliche Schulung für Hubarbeitsbühnen",
    "Bedienerausweis für Hubarbeitsbühnen",
    "Anschlagen von Lasten",
    "Atemschutzgeräte",
    "Brandschutzbeauftragter",
    "SiGeKo",
    "SCC Document 16",
    "SCC Document 17",
    "EHS Manager",
    "Sifa LEK 1-5",
    "Sifa LEK 6",
    "Sifa Nachweis",
    "Sifa Abschluss",
    "Sifa Lernbegleiter",
    "Sachkundenachweis TRGS 519",
  ];

  // Column aliases matching CreateCertificate
  const columnAliases = {
    vorname: ["vorname", "firstname", "first-name", "first name", "name"],
    nachname: ["nachname", "lastname", "last-name", "last name", "surname", "name"],
    gebDatum: ["gebdatum", "birthdate", "dateofbirth", "geb-datum", "birth-date", "geburtsdatum", "geburts-datum", "geb.-datum"],
    prufungsdatum: ["prufungsdatum", "examdate", "exam-date", "prufungs-datum", "prüfungsdatum", "prüfungs-datum", "prüfungsdatum", "pruefungsdatum"],
    prufungsort: ["prufungsort", "examlocation", "exam-location", "prufungs-ort", "prüfungsort", "prüfungs-ort", "prüfungsort", "pruefungsort"],
    gultigBis: ["gultigbis", "validuntil", "valid-until", "gultig-bis", "gültigbis", "gültig-bis", "gültig bis", "gueltigbis"],
    ausgestelltAm: ["ausgestelltam", "issuedon", "issued-on", "ausgestellt-am", "ausgestelltdatum", "ausgestellt am"],
    prufer: [
      "prufer", "examiner", "prufer-name", "prüfer", "prüfer-name", "pruefer",
      "lehrgangsleitung", "course management", "course director", "course lead",
    ],
    geschlecht: ["geschlecht", "gender", "sex"],
    startdatum: ["startdatum", "startdate", "start-date"],
    enddatum: ["enddatum", "enddate", "end-date"],
    branche: ["branche", "branch", "industry", "sector"],
    teilnehmerId: [
      "teilnehmerid",
      "teilnehmer-id",
      "teilnehmer_id",
      "participant-id",
      "participant_id",
      "participantid",
      "tn id",
      "tnid",
      "tn-id",
      "tn_id",
    ],
    ersatzDatum: [
      "ersatzdatum",
      "ersatz-datum",
      "replacement-date",
      "replacementdate",
      "ersatz datum",
    ],
  };

  const normalizeText = (text) => {
    if (!text || typeof text !== "string") return "";
    return text
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .replace(/[^a-z0-9]/g, "");
  };

  const convertDateToDDMMYYYY = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return dateStr;
    
    let date;
    if (dateStr instanceof Date) {
      date = dateStr;
    } else {
      const normalizedDateStr = dateStr.replace(/[-.]/g, "/");
      date = new Date(normalizedDateStr);
      
      if (isNaN(date.getTime())) {
        const parts = normalizedDateStr.split("/");
        if (parts.length === 3) {
          const [year, month, day] = parts;
          if (year.length === 4 && month.length <= 2 && day.length <= 2) {
            date = new Date(`${year}-${month}-${day}`);
          }
        }
      }
    }

    if (isNaN(date.getTime())) return dateStr;

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Hardcoded initial data - will be used if no data exists in localStorage
  const getInitialHardcodedData = () => [
    { id: 1, employeeName: "Vasilica Odica", trainingType: "PSAgA", dateOfIssue: "05.11.2025", examDate: "03.11.2025", validityPeriod: "1 year", validUntil: "02.11.2026", nextTrainingDate: "10.10.2026", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 2, employeeName: "Florian Niculae", trainingType: "PSAgA", dateOfIssue: "05.11.2025", examDate: "03.11.2025", validityPeriod: "1 year", validUntil: "02.11.2026", nextTrainingDate: "10.10.2026", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 3, employeeName: "Tudor Niculae", trainingType: "PSAgA", dateOfIssue: "05.11.2025", examDate: "03.11.2025", validityPeriod: "1 year", validUntil: "02.11.2026", nextTrainingDate: "10.10.2026", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 4, employeeName: "Cristian Aurel Manciu", trainingType: "PSAgA", dateOfIssue: "05.11.2025", examDate: "03.11.2025", validityPeriod: "1 year", validUntil: "02.11.2026", nextTrainingDate: "10.10.2026", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 5, employeeName: "Sirwan Asi Nader Lak", trainingType: "PSAgA", dateOfIssue: "05.11.2025", examDate: "03.11.2025", validityPeriod: "1 year", validUntil: "02.11.2026", nextTrainingDate: "10.10.2026", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 6, employeeName: "Dragan Simovski", trainingType: "PSAgA", dateOfIssue: "05.11.2025", examDate: "03.11.2025", validityPeriod: "1 year", validUntil: "02.11.2026", nextTrainingDate: "10.10.2026", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 7, employeeName: "Vasilica Odica", trainingType: "Atemschutzgeräte", dateOfIssue: "11.11.2025", examDate: "10.11.2026", validityPeriod: "1 year", validUntil: "09.11.2026", nextTrainingDate: "16.10.2026", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 8, employeeName: "Florian Niculae", trainingType: "Atemschutzgeräte", dateOfIssue: "11.11.2025", examDate: "10.11.2026", validityPeriod: "1 year", validUntil: "09.11.2026", nextTrainingDate: "16.10.2026", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 9, employeeName: "Tudor Niculae", trainingType: "Atemschutzgeräte", dateOfIssue: "11.11.2025", examDate: "10.11.2026", validityPeriod: "1 year", validUntil: "09.11.2026", nextTrainingDate: "16.10.2026", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 10, employeeName: "Cristian Aurel Manciu", trainingType: "Atemschutzgeräte", dateOfIssue: "11.11.2025", examDate: "10.11.2026", validityPeriod: "1 year", validUntil: "09.11.2026", nextTrainingDate: "16.10.2026", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 11, employeeName: "Sirwan Asi Nader Lak", trainingType: "Atemschutzgeräte", dateOfIssue: "11.11.2025", examDate: "10.11.2026", validityPeriod: "1 year", validUntil: "09.11.2026", nextTrainingDate: "16.10.2026", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 12, employeeName: "Dragan Simovski", trainingType: "SCC Document 16", dateOfIssue: "17.01.2025", examDate: "16.01.2025", validityPeriod: "5 years", validUntil: "15.01.2030", nextTrainingDate: "23.12.2029", status: { text: "Valid", class: "status-valid" }, remarks: "David Heckmann" },
    { id: 13, employeeName: "Vasilica Odica", trainingType: "SCC Document 16", dateOfIssue: "07.07.2025", examDate: "04.07.2030", validityPeriod: "5 years", validUntil: "03.07.2030", nextTrainingDate: "11.06.2030", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 14, employeeName: "Florian Niculae", trainingType: "SCC Document 16", dateOfIssue: "07.07.2025", examDate: "04.07.2030", validityPeriod: "5 years", validUntil: "03.07.2030", nextTrainingDate: "11.06.2030", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 15, employeeName: "Tudor Niculae", trainingType: "SCC Document 16", dateOfIssue: "07.07.2025", examDate: "04.07.2030", validityPeriod: "5 years", validUntil: "03.07.2030", nextTrainingDate: "11.06.2030", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 16, employeeName: "Cristian Aurel Manciu", trainingType: "SCC Document 16", dateOfIssue: "07.07.2025", examDate: "04.07.2030", validityPeriod: "5 years", validUntil: "03.07.2030", nextTrainingDate: "11.06.2030", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 17, employeeName: "Sirwan Asi Nader Lak", trainingType: "SCC Document 16", dateOfIssue: "07.07.2025", examDate: "04.07.2030", validityPeriod: "5 years", validUntil: "03.07.2030", nextTrainingDate: "11.06.2030", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
    { id: 18, employeeName: "Hani Vucenovic", trainingType: "SCC Document 17", dateOfIssue: "01.11.2021", examDate: "02.09.2021", validityPeriod: "5 years", validUntil: "31.10.2026", nextTrainingDate: "09.10.2026", status: { text: "Valid", class: "status-valid" }, remarks: "Snezana Heckmann" },
  ];

  // Load data from localStorage on mount, or use hardcoded data if no saved data exists
  useEffect(() => {
    const savedData = localStorage.getItem("trainingMatrixData");
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMatrixData(parsed);
          return;
        }
      } catch (error) {
        console.error("Error loading training matrix data:", error);
      }
    }
    // If no saved data, use hardcoded initial data
    const initialData = getInitialHardcodedData();
    setMatrixData(initialData);
    localStorage.setItem("trainingMatrixData", JSON.stringify(initialData));
  }, []);

  // Save data to localStorage whenever matrixData changes
  useEffect(() => {
    if (matrixData.length > 0) {
      localStorage.setItem("trainingMatrixData", JSON.stringify(matrixData));
    }
  }, [matrixData]);

  // Auto-calculate validity period based on training type
  useEffect(() => {
    if (formData.trainingType === "SCC Document 16" || formData.trainingType === "SCC Document 17") {
      setFormData((prev) => ({ ...prev, validityPeriod: "5" }));
    } else if (formData.trainingType) {
      setFormData((prev) => ({ ...prev, validityPeriod: "1" }));
    }
  }, [formData.trainingType]);

  // Auto-calculate next training date (only if date is in valid format)
  useEffect(() => {
    if (formData.dateOfIssue && formData.validityPeriod) {
      // Try to parse the date
      let issueDate;
      if (formData.dateOfIssue.includes(".")) {
        // DD.MM.YYYY format
        const [day, month, year] = formData.dateOfIssue.split(".");
        issueDate = new Date(`${year}-${month}-${day}`);
      } else {
        issueDate = new Date(formData.dateOfIssue);
      }
      
      if (!isNaN(issueDate.getTime())) {
        const nextDate = new Date(issueDate);
        nextDate.setFullYear(nextDate.getFullYear() + parseInt(formData.validityPeriod));
        const day = String(nextDate.getDate()).padStart(2, "0");
        const month = String(nextDate.getMonth() + 1).padStart(2, "0");
        const year = nextDate.getFullYear();
        setFormData((prev) => ({
          ...prev,
          nextTrainingDate: `${day}.${month}.${year}`,
        }));
      }
    }
  }, [formData.dateOfIssue, formData.validityPeriod]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.employeeName || !formData.trainingType || !formData.dateOfIssue) {
      alert(t("trainingMatrix.errors.requiredFields"));
      return;
    }

    // Parse date of issue
    let issueDate;
    if (formData.dateOfIssue.includes(".")) {
      // DD.MM.YYYY format
      const [day, month, year] = formData.dateOfIssue.split(".");
      issueDate = new Date(`${year}-${month}-${day}`);
    } else {
      issueDate = new Date(formData.dateOfIssue);
    }

    if (isNaN(issueDate.getTime())) {
      alert(t("trainingMatrix.errors.invalidDate"));
      return;
    }

    const validUntil = new Date(issueDate);
    validUntil.setFullYear(validUntil.getFullYear() + parseInt(formData.validityPeriod));

    const status = getStatus(validUntil);

    // Format dates for storage (keep as text format DD.MM.YYYY)
    const formatDateForStorage = (date) => {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    };

    // Format dateOfIssue to DD.MM.YYYY if not already
    let dateOfIssueStr = formData.dateOfIssue.trim();
    if (!dateOfIssueStr.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      // If not in DD.MM.YYYY format, try to parse and format
      dateOfIssueStr = formatDateForStorage(issueDate);
    }

    // Parse next training date if provided, otherwise calculate
    let nextTrainingDateStr = formData.nextTrainingDate.trim();
    if (!nextTrainingDateStr) {
      nextTrainingDateStr = formatDateForStorage(validUntil);
    } else {
      // Validate and format nextTrainingDate
      if (!nextTrainingDateStr.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
        // Try to parse and format
        let nextDate;
        if (nextTrainingDateStr.includes(".")) {
          const [day, month, year] = nextTrainingDateStr.split(".");
          nextDate = new Date(`${year}-${month}-${day}`);
        } else {
          nextDate = new Date(nextTrainingDateStr);
        }
        if (!isNaN(nextDate.getTime())) {
          nextTrainingDateStr = formatDateForStorage(nextDate);
        } else {
          // If invalid, use validUntil
          nextTrainingDateStr = formatDateForStorage(validUntil);
        }
      }
    }

    // Format exam date if provided
    let examDateStr = formData.examDate.trim();
    if (examDateStr && !examDateStr.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      // Try to parse and format
      let examDate;
      if (examDateStr.includes(".")) {
        const [day, month, year] = examDateStr.split(".");
        examDate = new Date(`${year}-${month}-${day}`);
      } else {
        examDate = new Date(examDateStr);
      }
      if (!isNaN(examDate.getTime())) {
        examDateStr = formatDateForStorage(examDate);
      } else {
        examDateStr = "";
      }
    }

    const newRow = {
      id: Date.now(),
      employeeName: formData.employeeName,
      trainingType: formData.trainingType,
      dateOfIssue: dateOfIssueStr, // Stored as DD.MM.YYYY
      examDate: examDateStr || "", // Stored as DD.MM.YYYY
      validityPeriod: `${formData.validityPeriod} ${parseInt(formData.validityPeriod) > 1 ? t("trainingMatrix.years") : t("trainingMatrix.year")}`,
      validUntil: formatDateForStorage(validUntil),
      nextTrainingDate: nextTrainingDateStr, // Stored as DD.MM.YYYY
      status,
      remarks: formData.remarks || "",
    };

    setMatrixData((prev) => [...prev, newRow]);
    setFormData({
      employeeName: "",
      trainingType: "",
      dateOfIssue: "",
      examDate: "",
      validityPeriod: "1",
      nextTrainingDate: "",
      remarks: "",
    });
  };

  const getStatus = (validUntil) => {
    const today = new Date();
    const daysUntilExpiry = Math.ceil((validUntil - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { text: t("trainingMatrix.status.expired"), class: "status-expired" };
    } else if (daysUntilExpiry <= 30) {
      return { text: t("trainingMatrix.status.expiringSoon"), class: "status-expiring" };
    } else {
      return { text: t("trainingMatrix.status.valid"), class: "status-valid" };
    }
  };

  const removeRow = (id) => {
    if (window.confirm(t("trainingMatrix.confirmRemove"))) {
      setMatrixData((prev) => prev.filter((row) => row.id !== id));
    }
  };

  const handleCellEdit = (rowId, field, value) => {
    setMatrixData((prev) => {
      return prev.map((row) => {
        if (row.id === rowId) {
          const updatedRow = { ...row };
          
          if (field === "dateOfIssue") {
            // Parse and validate date
            let issueDate;
            if (value.includes(".")) {
              const [day, month, year] = value.split(".");
              issueDate = new Date(`${year}-${month}-${day}`);
            } else {
              issueDate = new Date(value);
            }
            
            if (!isNaN(issueDate.getTime())) {
              updatedRow.dateOfIssue = value;
              
              // Recalculate validUntil and nextTrainingDate
              const validityYears = row.trainingType === "SCC Document 16" || row.trainingType === "SCC Document 17" ? 5 : 1;
              const validUntil = new Date(issueDate);
              validUntil.setFullYear(validUntil.getFullYear() + validityYears);
              
              const day = String(validUntil.getDate()).padStart(2, "0");
              const month = String(validUntil.getMonth() + 1).padStart(2, "0");
              const year = validUntil.getFullYear();
              updatedRow.validUntil = `${day}.${month}.${year}`;
              updatedRow.nextTrainingDate = `${day}.${month}.${year}`;
              
              // Update status
              updatedRow.status = getStatus(validUntil);
              
              // Update validity period text
              updatedRow.validityPeriod = `${validityYears} ${validityYears > 1 ? t("trainingMatrix.years") : t("trainingMatrix.year")}`;
            } else {
              updatedRow.dateOfIssue = value; // Still update even if invalid, user can fix it
            }
          } else if (field === "validUntil") {
            updatedRow.validUntil = value;
            // Recalculate status
            let validUntilDate;
            if (value.includes(".")) {
              const [day, month, year] = value.split(".");
              validUntilDate = new Date(`${year}-${month}-${day}`);
            } else {
              validUntilDate = new Date(value);
            }
            if (!isNaN(validUntilDate.getTime())) {
              updatedRow.status = getStatus(validUntilDate);
            }
          } else if (field === "nextTrainingDate") {
            updatedRow.nextTrainingDate = value;
          } else if (field === "trainingType") {
            updatedRow.trainingType = value;
            // Recalculate validity period based on training type
            let validityYears = 1;
            if (value === "SCC Document 16" || value === "SCC Document 17") {
              validityYears = 5;
            }
            updatedRow.validityPeriod = `${validityYears} ${validityYears > 1 ? t("trainingMatrix.years") : t("trainingMatrix.year")}`;
            
            // Recalculate dates if dateOfIssue exists
            if (updatedRow.dateOfIssue) {
              let issueDate;
              if (updatedRow.dateOfIssue.includes(".")) {
                const [day, month, year] = updatedRow.dateOfIssue.split(".");
                issueDate = new Date(`${year}-${month}-${day}`);
              } else {
                issueDate = new Date(updatedRow.dateOfIssue);
              }
              
              if (!isNaN(issueDate.getTime())) {
                const validUntil = new Date(issueDate);
                validUntil.setFullYear(validUntil.getFullYear() + validityYears);
                
                const day = String(validUntil.getDate()).padStart(2, "0");
                const month = String(validUntil.getMonth() + 1).padStart(2, "0");
                const year = validUntil.getFullYear();
                updatedRow.validUntil = `${day}.${month}.${year}`;
                updatedRow.nextTrainingDate = `${day}.${month}.${year}`;
                updatedRow.status = getStatus(validUntil);
              }
            }
          } else {
            updatedRow[field] = value;
          }
          
          return updatedRow;
        }
        return row;
      });
    });
  };

  const handleCellBlur = () => {
    setEditingCell(null);
  };

  const handleCellFocus = (rowId, field) => {
    setEditingCell({ rowId, field });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    
    // If already in DD.MM.YYYY format, return as is
    if (typeof dateString === "string" && dateString.includes(".") && dateString.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      return dateString;
    }
    
    // Try to parse the date
    let date;
    if (dateString.includes(".")) {
      // DD.MM.YYYY format
      const [day, month, year] = dateString.split(".");
      date = new Date(`${year}-${month}-${day}`);
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) return dateString; // Return original if can't parse
    
    // Always return DD.MM.YYYY format
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const handleExcelUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to array of arrays (same as CreateCertificate)
        const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:A1");
        const sheetData = [];
        for (let row = range.s.r; row <= range.e.r; row++) {
          const rowData = [];
          for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            const cell = worksheet[cellAddress];
            rowData.push(cell ? cell.v : "");
          }
          sheetData.push(rowData);
        }

        if (sheetData.length < 2) {
          setStatus(t("trainingMatrix.errors.invalidFile"));
          return;
        }

        // Find header row (same logic as CreateCertificate)
        let headerRowIndex = -1;
        for (let i = 0; i < sheetData.length; i++) {
          const row = sheetData[i];
          const normalizedRow = row.map((cell) => normalizeText(cell));
          const hasKnownColumn = normalizedRow.some((cell) =>
            Object.values(columnAliases).some((aliases) =>
              aliases.includes(cell)
            )
          );
          if (hasKnownColumn) {
            headerRowIndex = i;
            break;
          }
        }

        if (headerRowIndex === -1) {
          setStatus(t("trainingMatrix.errors.noHeadersFound"));
          return;
        }

        const headers = sheetData[headerRowIndex].map((header) => normalizeText(header));

        // Map headers to fields (same as CreateCertificate)
        const fieldMapping = {};
        const hasVorname = headers.some((header) => columnAliases.vorname.includes(header));
        const hasNachname = headers.some((header) => columnAliases.nachname.includes(header));
        const hasLastname = headers.some((header) => ["lastname", "last-name", "last name", "surname"].includes(header));
        
        // Check if file has Training Title column (from exported matrix)
        // Check for both English and German translations
        const trainingTitleIdx = headers.findIndex((h) => {
          const normalized = normalizeText(h);
          return normalized.includes("training") && normalized.includes("title") ||
                 normalized.includes("schulung") && normalized.includes("titel") ||
                 normalized === "schulungstitel" ||
                 normalized === "training title";
        });
        const hasTrainingTitleColumn = trainingTitleIdx !== -1;
        
        // Check if file has Employee Name column (from exported matrix)
        const employeeNameIdx = headers.findIndex((h) => {
          const normalized = normalizeText(h);
          return (normalized.includes("employee") && normalized.includes("name")) || 
                 (normalized.includes("mitarbeiter") && normalized.includes("name")) ||
                 normalized === "mitarbeitername" ||
                 normalized === "employee name";
        });
        const hasEmployeeNameColumn = employeeNameIdx !== -1;
        
        // Check for Date of Issue column (from exported matrix)
        const dateOfIssueIdx = headers.findIndex((h) => {
          const normalized = normalizeText(h);
          return (normalized.includes("date") && normalized.includes("issue")) ||
                 (normalized.includes("ausstellungsdatum")) ||
                 normalized === "ausstellungsdatum" ||
                 normalized === "date of issue";
        });
        
        // Check for Exam Date column (from exported matrix)
        const examDateIdx = headers.findIndex((h) => {
          const normalized = normalizeText(h);
          return (normalized.includes("exam") && normalized.includes("date")) ||
                 (normalized.includes("prüfung") && normalized.includes("datum")) ||
                 normalized === "prüfungsdatum" ||
                 normalized === "exam date";
        });
        
        // Check for other exported matrix columns
        const validUntilIdx = headers.findIndex((h) => {
          const normalized = normalizeText(h);
          return (normalized.includes("valid") && normalized.includes("until")) || 
                 (normalized.includes("gültig") && normalized.includes("bis")) ||
                 normalized === "gültig bis" ||
                 normalized === "valid until";
        });
        const nextTrainingDateIdx = headers.findIndex((h) => {
          const normalized = normalizeText(h);
          return (normalized.includes("next") && normalized.includes("training")) || 
                 (normalized.includes("nächstes") && normalized.includes("schulung")) ||
                 normalized === "nächstes schulungsdatum" ||
                 normalized === "next training date";
        });
        const remarksIdx = headers.findIndex((h) => {
          const normalized = normalizeText(h);
          return normalized.includes("remark") || 
                 normalized.includes("bemerkung") ||
                 normalized === "bemerkungen" ||
                 normalized === "remarks";
        });

        headers.forEach((header, idx) => {
          // Handle special case for "name" column
          if (hasVorname && header === "name" && !hasNachname) {
            fieldMapping.nachname = idx;
          } else if (hasLastname && header === "name" && !hasVorname) {
            fieldMapping.vorname = idx;
          } else {
            Object.entries(columnAliases).forEach(([field, aliases]) => {
              if (aliases.includes(header)) {
                if (fieldMapping[field] === undefined) {
                  fieldMapping[field] = idx;
                }
              }
            });
          }
        });
        
        // Add exported matrix column mappings if they exist
        if (hasTrainingTitleColumn) {
          fieldMapping.trainingTitle = trainingTitleIdx;
        }
        if (hasEmployeeNameColumn) {
          fieldMapping.employeeName = employeeNameIdx;
        }
        if (dateOfIssueIdx !== -1) {
          fieldMapping.dateOfIssue = dateOfIssueIdx;
        }
        if (examDateIdx !== -1) {
          fieldMapping.examDate = examDateIdx;
        }
        if (validUntilIdx !== -1) {
          fieldMapping.validUntil = validUntilIdx;
        }
        if (nextTrainingDateIdx !== -1) {
          fieldMapping.nextTrainingDate = nextTrainingDateIdx;
        }
        if (remarksIdx !== -1) {
          fieldMapping.remarks = remarksIdx;
        }

        // Process data rows
        const dataRows = sheetData.slice(headerRowIndex + 1);
        const newRows = [];
        let skippedRows = 0;

        dataRows.forEach((row, rowIndex) => {
          // Check if this is exported matrix format (has Training Title column)
          let trainingType = selectedCertificateType;
          if (hasTrainingTitleColumn && fieldMapping.trainingTitle !== undefined) {
            const trainingTitle = row[fieldMapping.trainingTitle]?.toString().trim() || "";
            if (trainingTitle) {
              trainingType = trainingTitle;
            }
          }
          
          // If no training type found and no selected type, try to get from employee name column or skip
          if (!trainingType) {
            // Try to detect from other columns or use selected type
            if (!selectedCertificateType) {
              skippedRows++;
              return;
            }
            trainingType = selectedCertificateType;
          }

          const vorname = fieldMapping.vorname !== undefined ? (row[fieldMapping.vorname]?.toString().trim() || "") : "";
          const nachname = fieldMapping.nachname !== undefined ? (row[fieldMapping.nachname]?.toString().trim() || "") : "";
          
          // For exported matrix format, check dateOfIssue column first, then ausgestelltAm
          let ausgestelltAm = "";
          if (fieldMapping.dateOfIssue !== undefined) {
            ausgestelltAm = row[fieldMapping.dateOfIssue]?.toString().trim() || "";
          } else if (fieldMapping.ausgestelltAm !== undefined) {
            ausgestelltAm = row[fieldMapping.ausgestelltAm]?.toString().trim() || "";
          }
          
          // Get exam date from file
          const examDateFromFile = fieldMapping.examDate !== undefined ? (row[fieldMapping.examDate]?.toString().trim() || "") : "";
          
          const gultigBis = fieldMapping.gultigBis !== undefined ? (row[fieldMapping.gultigBis]?.toString().trim() || "") : "";
          const validUntilFromFile = fieldMapping.validUntil !== undefined ? (row[fieldMapping.validUntil]?.toString().trim() || "") : "";
          const nextTrainingDateFromFile = fieldMapping.nextTrainingDate !== undefined ? (row[fieldMapping.nextTrainingDate]?.toString().trim() || "") : "";
          const remarksFromFile = fieldMapping.remarks !== undefined ? (row[fieldMapping.remarks]?.toString().trim() || "") : "";

          // For exported matrix format, employee name might be in one column
          let employeeName = "";
          if (hasEmployeeNameColumn && fieldMapping.employeeName !== undefined) {
            // Use employee name column from exported matrix
            employeeName = row[fieldMapping.employeeName]?.toString().trim() || "";
          } else if (vorname && nachname) {
            // Combine first and last name
            employeeName = `${vorname} ${nachname}`.trim();
          }

          // Skip if no name or date
          if (!employeeName || !ausgestelltAm) {
            skippedRows++;
            return;
          }

          // Parse date of issue - ensure it's stored as DD.MM.YYYY
          let issueDate;
          let dateOfIssueStr = "";
          
          if (ausgestelltAm instanceof Date) {
            issueDate = ausgestelltAm;
          } else if (typeof ausgestelltAm === "string") {
            // Check if already in DD.MM.YYYY format
            if (ausgestelltAm.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
              // Already in correct format, parse to Date for validation
              const [day, month, year] = ausgestelltAm.split(".");
              issueDate = new Date(`${year}-${month}-${day}`);
              dateOfIssueStr = ausgestelltAm; // Keep original format
            } else {
              // Convert to DD.MM.YYYY format
              dateOfIssueStr = convertDateToDDMMYYYY(ausgestelltAm);
              // Parse to Date for validation
              if (dateOfIssueStr.includes(".")) {
                const [day, month, year] = dateOfIssueStr.split(".");
                issueDate = new Date(`${year}-${month}-${day}`);
              } else {
                issueDate = new Date(ausgestelltAm);
              }
            }
          } else {
            issueDate = new Date(ausgestelltAm);
          }

          if (isNaN(issueDate.getTime())) {
            skippedRows++;
            return;
          }
          
          // Ensure dateOfIssueStr is in DD.MM.YYYY format
          if (!dateOfIssueStr) {
            const day = String(issueDate.getDate()).padStart(2, "0");
            const month = String(issueDate.getMonth() + 1).padStart(2, "0");
            const year = issueDate.getFullYear();
            dateOfIssueStr = `${day}.${month}.${year}`;
          }

          // Determine validity period based on training type
          let validityPeriod = "1";
          if (trainingType === "SCC Document 16" || trainingType === "SCC Document 17") {
            validityPeriod = "5";
          }

          // Parse valid until date (if available, otherwise calculate) - ensure DD.MM.YYYY format
          let validUntil;
          let validUntilStr = "";
          
          // Priority: validUntilFromFile > gultigBis > calculate
          if (validUntilFromFile) {
            if (validUntilFromFile instanceof Date) {
              validUntil = validUntilFromFile;
            } else if (typeof validUntilFromFile === "string" && validUntilFromFile.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
              // Already in DD.MM.YYYY format
              const [day, month, year] = validUntilFromFile.split(".");
              validUntil = new Date(`${year}-${month}-${day}`);
              validUntilStr = validUntilFromFile;
            } else {
              validUntilStr = convertDateToDDMMYYYY(validUntilFromFile);
              if (validUntilStr.includes(".")) {
                const [day, month, year] = validUntilStr.split(".");
                validUntil = new Date(`${year}-${month}-${day}`);
              } else {
                validUntil = new Date(validUntilFromFile);
              }
            }
          } else if (gultigBis) {
            if (gultigBis instanceof Date) {
              validUntil = gultigBis;
            } else if (typeof gultigBis === "string" && gultigBis.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
              // Already in DD.MM.YYYY format
              const [day, month, year] = gultigBis.split(".");
              validUntil = new Date(`${year}-${month}-${day}`);
              validUntilStr = gultigBis;
            } else {
              validUntilStr = convertDateToDDMMYYYY(gultigBis);
              if (validUntilStr.includes(".")) {
                const [day, month, year] = validUntilStr.split(".");
                validUntil = new Date(`${year}-${month}-${day}`);
              } else {
                validUntil = new Date(gultigBis);
              }
            }
          }
          
          if (!validUntil || isNaN(validUntil.getTime())) {
            // Calculate from issue date
            validUntil = new Date(issueDate);
            validUntil.setFullYear(validUntil.getFullYear() + parseInt(validityPeriod));
          }
          
          // Format validUntil as DD.MM.YYYY if not already formatted
          if (!validUntilStr) {
            const day = String(validUntil.getDate()).padStart(2, "0");
            const month = String(validUntil.getMonth() + 1).padStart(2, "0");
            const year = validUntil.getFullYear();
            validUntilStr = `${day}.${month}.${year}`;
          }

          // Parse next training date (if available, otherwise use validUntil) - ensure DD.MM.YYYY format
          let nextTrainingDateStr = "";
          if (nextTrainingDateFromFile) {
            if (nextTrainingDateFromFile instanceof Date) {
              // Convert Date object to DD.MM.YYYY
              const day = String(nextTrainingDateFromFile.getDate()).padStart(2, "0");
              const month = String(nextTrainingDateFromFile.getMonth() + 1).padStart(2, "0");
              const year = nextTrainingDateFromFile.getFullYear();
              nextTrainingDateStr = `${day}.${month}.${year}`;
            } else if (typeof nextTrainingDateFromFile === "string" && nextTrainingDateFromFile.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
              // Already in DD.MM.YYYY format
              nextTrainingDateStr = nextTrainingDateFromFile;
            } else {
              // Convert to DD.MM.YYYY format
              nextTrainingDateStr = convertDateToDDMMYYYY(nextTrainingDateFromFile);
              // Validate the conversion
              if (!nextTrainingDateStr.includes(".")) {
                const nextDate = new Date(nextTrainingDateFromFile);
                if (!isNaN(nextDate.getTime())) {
                  const day = String(nextDate.getDate()).padStart(2, "0");
                  const month = String(nextDate.getMonth() + 1).padStart(2, "0");
                  const year = nextDate.getFullYear();
                  nextTrainingDateStr = `${day}.${month}.${year}`;
                }
              }
            }
          }
          
          if (!nextTrainingDateStr) {
            // Use validUntil as next training date
            nextTrainingDateStr = validUntilStr;
          }

          const status = getStatus(validUntil);

          // Parse exam date (if available) - ensure DD.MM.YYYY format
          let examDateStr = "";
          if (examDateFromFile) {
            if (examDateFromFile instanceof Date) {
              const day = String(examDateFromFile.getDate()).padStart(2, "0");
              const month = String(examDateFromFile.getMonth() + 1).padStart(2, "0");
              const year = examDateFromFile.getFullYear();
              examDateStr = `${day}.${month}.${year}`;
            } else if (typeof examDateFromFile === "string" && examDateFromFile.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
              examDateStr = examDateFromFile;
            } else {
              examDateStr = convertDateToDDMMYYYY(examDateFromFile);
            }
          }

          // Get remarks from file or from other fields
          let remarks = remarksFromFile;
          if (!remarks) {
            const prufer = fieldMapping.prufer !== undefined ? (row[fieldMapping.prufer]?.toString().trim() || "") : "";
            const prufungsort = fieldMapping.prufungsort !== undefined ? (row[fieldMapping.prufungsort]?.toString().trim() || "") : "";
            remarks = prufer || prufungsort || "";
          }

          newRows.push({
            id: Date.now() + rowIndex,
            employeeName,
            trainingType: trainingType,
            dateOfIssue: dateOfIssueStr, // Already in DD.MM.YYYY format
            examDate: examDateStr, // Already in DD.MM.YYYY format
            validityPeriod: `${validityPeriod} ${parseInt(validityPeriod) > 1 ? t("trainingMatrix.years") : t("trainingMatrix.year")}`,
            validUntil: validUntilStr, // Already in DD.MM.YYYY format
            nextTrainingDate: nextTrainingDateStr, // Already in DD.MM.YYYY format
            status,
            remarks,
          });
        });

        if (newRows.length === 0) {
          setStatus(t("trainingMatrix.errors.noValidRows"));
          return;
        }

        setMatrixData((prev) => [...prev, ...newRows]);
        const successMsg = skippedRows > 0
          ? `${t("trainingMatrix.success.imported", { count: newRows.length })} (${skippedRows} ${t("trainingMatrix.skippedRows")})`
          : t("trainingMatrix.success.imported", { count: newRows.length });
        setStatus(successMsg);
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } catch (error) {
        console.error("Error reading Excel file:", error);
        setStatus(t("trainingMatrix.errors.fileUploadFailed"));
      }
    };

    reader.onerror = () => {
      setStatus(t("trainingMatrix.errors.fileUploadFailed"));
    };

    reader.readAsArrayBuffer(uploadedFile);
  };

  const exportToExcel = () => {
    if (matrixData.length === 0) {
      alert(t("trainingMatrix.errors.noData"));
      return;
    }

    const worksheetData = [
      [
        t("trainingMatrix.table.employeeName"),
        t("trainingMatrix.table.trainingTitle"),
        t("trainingMatrix.table.dateOfIssue"),
        t("trainingMatrix.table.examDate"),
        t("trainingMatrix.table.validityPeriod"),
        t("trainingMatrix.table.validUntil"),
        t("trainingMatrix.table.nextTrainingDate"),
        t("trainingMatrix.table.status"),
        t("trainingMatrix.table.remarks"),
      ],
    ];

    matrixData.forEach((row) => {
      worksheetData.push([
        row.employeeName,
        row.trainingType,
        formatDate(row.dateOfIssue), // Already in DD.MM.YYYY format
        formatDate(row.examDate || ""), // Exam date in DD.MM.YYYY format
        row.validityPeriod,
        formatDate(row.validUntil), // Already in DD.MM.YYYY format
        formatDate(row.nextTrainingDate), // Already in DD.MM.YYYY format
        row.status.text,
        row.remarks || "",
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Training Matrix");

    const fileName = `Training_Matrix_APM_GmbH_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const exportToPDF = async () => {
    if (matrixData.length === 0) {
      alert(t("trainingMatrix.errors.noData"));
      return;
    }

    // Force German language for PDF
    const originalLang = i18n.language;
    i18n.changeLanguage("de");

    const doc = new jsPDF("landscape", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Professional header with border
    doc.setDrawColor(20, 184, 166);
    doc.setLineWidth(0.5);
    doc.line(14, 32, pageWidth - 14, 32); // Header border line
    
    // Load and add APM logo
    try {
      const logoUrl = "/apm_logo.png";
      
      // Fetch image and convert to base64
      const response = await fetch(logoUrl);
      const blob = await response.blob();
      const reader = new FileReader();
      
      const logoData = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Add logo to the header (right side) - larger and better positioned
      const logoWidth = 45; // mm
      const logoHeight = 17; // mm
      const logoX = pageWidth - logoWidth - 14; // Right margin
      const logoY = 12; // Top margin
      
      doc.addImage(logoData, "PNG", logoX, logoY, logoWidth, logoHeight);
    } catch (error) {
      console.warn("Could not load APM logo:", error);
      // Continue without logo if it fails to load
    }
    
    // Add title (left side, adjusted for logo) - German with better styling
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(20, 184, 166);
    doc.text(t("trainingMatrix.title"), 14, 22);
    
    // Add subtitle - German with better styling
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(t("trainingMatrix.subtitle"), 14, 28);

    // Prepare table data - use German status text
    const tableData = matrixData.map((row) => {
      // Get German status text
      let statusText = row.status.text;
      if (row.status.class === "status-valid") {
        statusText = "Gültig";
      } else if (row.status.class === "status-expiring") {
        statusText = "Läuft bald ab";
      } else if (row.status.class === "status-expired") {
        statusText = "Abgelaufen";
      }
      
      // Ensure dates are in DD.MM.YYYY format for PDF
      const formatDateForPDF = (dateStr) => {
        if (!dateStr || dateStr === "-") return "-";
        
        // If already in DD.MM.YYYY format, return as is
        if (typeof dateStr === "string" && dateStr.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
          return dateStr;
        }
        
        // Try to parse and format
        let date;
        if (dateStr.includes(".")) {
          // DD.MM.YYYY format
          const parts = dateStr.split(".");
          if (parts.length === 3) {
            const [day, month, year] = parts;
            date = new Date(`${year}-${month}-${day}`);
          } else {
            date = new Date(dateStr);
          }
        } else if (dateStr.includes("-")) {
          // ISO format (YYYY-MM-DD)
          date = new Date(dateStr);
        } else {
          date = new Date(dateStr);
        }
        
        if (isNaN(date.getTime())) return dateStr; // Return original if can't parse
        
        // Format as DD.MM.YYYY
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
      };
      
      return [
        row.employeeName,
        row.trainingType,
        formatDateForPDF(row.dateOfIssue),
        formatDateForPDF(row.examDate || ""),
        row.validityPeriod,
        formatDateForPDF(row.validUntil),
        formatDateForPDF(row.nextTrainingDate),
        statusText,
        row.remarks || "-",
      ];
    });

    // Add professional table with better styling
    autoTable(doc, {
      startY: 37,
      head: [
        [
          t("trainingMatrix.table.employeeName"),
          t("trainingMatrix.table.trainingTitle"),
          t("trainingMatrix.table.dateOfIssue"),
          t("trainingMatrix.table.examDate"),
          t("trainingMatrix.table.validityPeriod"),
          t("trainingMatrix.table.validUntil"),
          t("trainingMatrix.table.nextTrainingDate"),
          t("trainingMatrix.table.status"),
          t("trainingMatrix.table.remarks"),
        ],
      ],
      body: tableData,
      theme: "striped",
      headStyles: {
        fillColor: [20, 184, 166],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 8,
        halign: "center",
        valign: "middle",
        cellPadding: { top: 4, bottom: 4, left: 2, right: 2 },
      },
      bodyStyles: {
        fontSize: 7.5,
        halign: "left",
        valign: "top",
        cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
        overflow: "linebreak",
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250],
      },
      styles: {
        cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
        overflow: "linebreak",
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
        font: "helvetica",
        fontSize: 7.5,
        textColor: [50, 50, 50],
      },
      columnStyles: {
        0: { cellWidth: 29, fontStyle: "bold", halign: "left" }, // Employee Name - "Mitarbeitername"
        1: { cellWidth: 25, halign: "left" }, // Training Title - "Schulungstitel"
        2: { cellWidth: 34, halign: "center" }, // Date of Issue - "Ausstellungsdatum"
        3: { cellWidth: 30, halign: "center" }, // Exam Date - "Prüfungsdatum"
        4: { cellWidth: 32, halign: "center" }, // Validity Period - "Gültigkeitsdauer"
        5: { cellWidth: 23, halign: "center" }, // Valid Until - "Gültig bis"
        6: { cellWidth: 33, halign: "center" }, // Next Training Date - "Nächstes Schulungsdatum"
        7: { cellWidth: 19, halign: "center" }, // Status - "Status"
        8: { cellWidth: 29, halign: "left" }, // Remarks - "Bemerkungen"
      },
      // Calculate total table width and center it
      // Total column width: 29+25+34+30+32+23+33+19+29 = 254mm
      // Page width (landscape A4): 297mm
      // Center margin: (297 - 254) / 2 = 21.5mm
      margin: { top: 40, left: 21.5, right: 21.5, bottom: 20 },
      tableWidth: 254, // Explicit table width to match column widths
      showHead: "everyPage",
    });

    // Add professional footer - German (without date)
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Footer border line
      doc.setDrawColor(20, 184, 166);
      doc.setLineWidth(0.5);
      doc.line(14, pageHeight - 18, pageWidth - 14, pageHeight - 18);
      
      // Footer text
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Seite ${i} von ${pageCount}`,
        pageWidth / 2,
        pageHeight - 12,
        { align: "center" }
      );
      
      // Company info in footer (optional)
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        "APM GmbH",
        pageWidth - 14,
        pageHeight - 12,
        { align: "right" }
      );
    }

    const fileName = `Schulungsmatrix_APM_GmbH_${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
    
    // Restore original language
    i18n.changeLanguage(originalLang);
  };

  return (
    <div className="training-matrix-container">
      <header className="matrix-header">
        <div className="header-content">
          <h1>
            {t("trainingMatrix.title")}
            <span className="beta-badge">BETA</span>
          </h1>
        </div>
      </header>

      <main className="matrix-main">
        <div className="info-box">
          <p>
            <strong>{t("trainingMatrix.validityPeriods.title")}</strong>
          </p>
          <p>{t("trainingMatrix.validityPeriods.description")}</p>
        </div>

        <div className="form-section">
          <h2>
            <span>📝</span>
            {t("trainingMatrix.addEmployee.title")}
          </h2>
          {status && (
            <div className={`status-message ${status.includes("successfully") || status.includes("imported") ? "success" : "error"}`}>
              <span>{status.includes("successfully") || status.includes("imported") ? "✓" : "⚠"}</span>
              <span>{status}</span>
            </div>
          )}
          <div className="upload-section">
            <h3 style={{ marginTop: 0, marginBottom: "15px", fontSize: "16px", fontWeight: 600, color: "var(--text-primary)" }}>
              📤 {t("trainingMatrix.importSection.title") || "Import Data"}
            </h3>
            <div className="form-group">
              <label htmlFor="certificateTypeSelect">{t("trainingMatrix.form.selectCertificateTypeForImport")}</label>
              <select
                id="certificateTypeSelect"
                className="form-select"
                value={selectedCertificateType}
                onChange={(e) => setSelectedCertificateType(e.target.value)}
              >
                <option value="">{t("trainingMatrix.form.selectCertificateType")} ({t("trainingMatrix.form.optionalForExportedFiles")})</option>
                {certificateTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <small className="form-hint">{t("trainingMatrix.importHint")}</small>
            </div>
            <label htmlFor="excelUpload" className="upload-label">
              <Upload size={16} />
              {t("trainingMatrix.buttons.uploadExcel")}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              id="excelUpload"
              accept=".xlsx,.xls"
              onChange={handleExcelUpload}
              style={{ display: "none" }}
            />
            <span className="upload-hint">{t("trainingMatrix.uploadHint")}</span>
          </div>
          <div style={{ marginTop: "30px", paddingTop: "25px", borderTop: "2px solid var(--border)" }}>
            <h3 style={{ marginTop: 0, marginBottom: "25px", fontSize: "16px", fontWeight: 600, color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "8px" }}>
              <span>✏️</span>
              <span>{t("trainingMatrix.manualEntry.title")}</span>
            </h3>
          </div>
          <form id="addEmployeeForm" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="employeeName">{t("trainingMatrix.form.employeeName")} *</label>
                <input
                  type="text"
                  id="employeeName"
                  name="employeeName"
                  className="form-input"
                  placeholder={t("trainingMatrix.form.employeeNamePlaceholder")}
                  value={formData.employeeName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="trainingType">{t("trainingMatrix.form.trainingType")} *</label>
                <select
                  id="trainingType"
                  name="trainingType"
                  className="form-select"
                  value={formData.trainingType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">{t("trainingMatrix.form.selectTrainingType")}</option>
                  <option value="SCC Document 16">SCC Document 16</option>
                  <option value="SCC Document 17">SCC Document 17</option>
                  <option value="Respiratory Protection">
                    {t("trainingMatrix.form.respiratoryProtection")}
                  </option>
                  <option value="Fall Protection">{t("trainingMatrix.form.fallProtection")}</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="dateOfIssue">{t("trainingMatrix.form.dateOfIssue")} *</label>
                <input
                  type="text"
                  id="dateOfIssue"
                  name="dateOfIssue"
                  className="form-input"
                  placeholder="DD.MM.YYYY"
                  value={formData.dateOfIssue}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="examDate">{t("trainingMatrix.form.examDate")}</label>
                <input
                  type="text"
                  id="examDate"
                  name="examDate"
                  className="form-input"
                  placeholder="DD.MM.YYYY"
                  value={formData.examDate}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="validityPeriod">{t("trainingMatrix.form.validityPeriod")}</label>
                <select
                  id="validityPeriod"
                  name="validityPeriod"
                  className="form-select"
                  value={formData.validityPeriod}
                  onChange={handleInputChange}
                >
                  <option value="1">1 {t("trainingMatrix.year")}</option>
                  <option value="5">5 {t("trainingMatrix.years")}</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="nextTrainingDate">{t("trainingMatrix.form.nextTrainingDate")}</label>
                <input
                  type="text"
                  id="nextTrainingDate"
                  name="nextTrainingDate"
                  className="form-input"
                  placeholder="DD.MM.YYYY"
                  value={formData.nextTrainingDate}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="remarks">{t("trainingMatrix.form.remarks")}</label>
                <input
                  type="text"
                  id="remarks"
                  name="remarks"
                  className="form-input"
                  placeholder={t("trainingMatrix.form.remarksPlaceholder")}
                  value={formData.remarks}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="btn-group">
              <button type="submit" className="btn btn-primary">
                <Plus size={16} />
                {t("trainingMatrix.buttons.addEmployee")}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  setFormData({
                    employeeName: "",
                    trainingType: "",
                    dateOfIssue: "",
                    examDate: "",
                    validityPeriod: "1",
                    nextTrainingDate: "",
                    remarks: "",
                  })
                }
              >
                {t("trainingMatrix.buttons.clearForm")}
              </button>
            </div>
          </form>
        </div>

        <div className="form-section">
          <div className="export-section">
            <h2>{t("trainingMatrix.matrix.title")}</h2>
            <div className="export-buttons">
              <button type="button" className="btn btn-success" onClick={exportToExcel}>
                <Download size={16} />
                {t("trainingMatrix.buttons.exportToExcel")}
              </button>
              <button type="button" className="btn btn-primary" onClick={exportToPDF}>
                <FileText size={16} />
                {t("trainingMatrix.buttons.exportToPDF")}
              </button>
            </div>
          </div>
          <div className="matrix-table-wrapper">
            <table className="matrix-table" id="matrixTable">
              <thead>
                <tr>
                  <th>{t("trainingMatrix.table.employeeName")}</th>
                  <th>{t("trainingMatrix.table.trainingTitle")}</th>
                  <th>{t("trainingMatrix.table.dateOfIssue")}</th>
                  <th>{t("trainingMatrix.table.examDate")}</th>
                  <th>{t("trainingMatrix.table.validityPeriod")}</th>
                  <th>{t("trainingMatrix.table.validUntil")}</th>
                  <th>{t("trainingMatrix.table.nextTrainingDate")}</th>
                  <th>{t("trainingMatrix.table.status")}</th>
                  <th>{t("trainingMatrix.table.remarks")}</th>
                  <th className="actions-column">{t("trainingMatrix.table.actions")}</th>
                </tr>
              </thead>
              <tbody id="matrixTableBody">
                {matrixData.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="empty-state">
                      {t("trainingMatrix.emptyState")}
                    </td>
                  </tr>
                ) : (
                  matrixData.map((row) => (
                    <tr key={row.id}>
                      <td className="employee-name">
                        {editingCell?.rowId === row.id && editingCell?.field === "employeeName" ? (
                          <input
                            type="text"
                            className="table-edit-input"
                            value={row.employeeName}
                            onChange={(e) => handleCellEdit(row.id, "employeeName", e.target.value)}
                            onBlur={handleCellBlur}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleCellBlur();
                            }}
                            autoFocus
                          />
                        ) : (
                          <span
                            onClick={() => handleCellFocus(row.id, "employeeName")}
                            className="editable-cell"
                            title="Click to edit"
                          >
                            {row.employeeName}
                          </span>
                        )}
                      </td>
                      <td>
                        {editingCell?.rowId === row.id && editingCell?.field === "trainingType" ? (
                          <select
                            className="table-edit-select"
                            value={row.trainingType}
                            onChange={(e) => handleCellEdit(row.id, "trainingType", e.target.value)}
                            onBlur={handleCellBlur}
                            autoFocus
                          >
                            {certificateTypes.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            onClick={() => handleCellFocus(row.id, "trainingType")}
                            className="editable-cell"
                            title="Click to edit"
                          >
                            {row.trainingType}
                          </span>
                        )}
                      </td>
                      <td>
                        {editingCell?.rowId === row.id && editingCell?.field === "dateOfIssue" ? (
                          <input
                            type="text"
                            className="table-edit-input"
                            placeholder="DD.MM.YYYY"
                            value={row.dateOfIssue}
                            onChange={(e) => handleCellEdit(row.id, "dateOfIssue", e.target.value)}
                            onBlur={handleCellBlur}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleCellBlur();
                            }}
                            autoFocus
                          />
                        ) : (
                          <span
                            onClick={() => handleCellFocus(row.id, "dateOfIssue")}
                            className="editable-cell"
                            title="Click to edit"
                          >
                            {formatDate(row.dateOfIssue)}
                          </span>
                        )}
                      </td>
                      <td>
                        {editingCell?.rowId === row.id && editingCell?.field === "examDate" ? (
                          <input
                            type="text"
                            className="table-edit-input"
                            placeholder="DD.MM.YYYY"
                            value={row.examDate || ""}
                            onChange={(e) => handleCellEdit(row.id, "examDate", e.target.value)}
                            onBlur={handleCellBlur}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleCellBlur();
                            }}
                            autoFocus
                          />
                        ) : (
                          <span
                            onClick={() => handleCellFocus(row.id, "examDate")}
                            className="editable-cell"
                            title="Click to edit"
                          >
                            {formatDate(row.examDate || "")}
                          </span>
                        )}
                      </td>
                      <td>{row.validityPeriod}</td>
                      <td>
                        {editingCell?.rowId === row.id && editingCell?.field === "validUntil" ? (
                          <input
                            type="text"
                            className="table-edit-input"
                            placeholder="DD.MM.YYYY"
                            value={row.validUntil}
                            onChange={(e) => handleCellEdit(row.id, "validUntil", e.target.value)}
                            onBlur={handleCellBlur}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleCellBlur();
                            }}
                            autoFocus
                          />
                        ) : (
                          <span
                            onClick={() => handleCellFocus(row.id, "validUntil")}
                            className="editable-cell"
                            title="Click to edit"
                          >
                            {formatDate(row.validUntil)}
                          </span>
                        )}
                      </td>
                      <td>
                        {editingCell?.rowId === row.id && editingCell?.field === "nextTrainingDate" ? (
                          <input
                            type="text"
                            className="table-edit-input"
                            placeholder="DD.MM.YYYY"
                            value={row.nextTrainingDate}
                            onChange={(e) => handleCellEdit(row.id, "nextTrainingDate", e.target.value)}
                            onBlur={handleCellBlur}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleCellBlur();
                            }}
                            autoFocus
                          />
                        ) : (
                          <span
                            onClick={() => handleCellFocus(row.id, "nextTrainingDate")}
                            className="editable-cell"
                            title="Click to edit"
                          >
                            {formatDate(row.nextTrainingDate)}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`status-badge ${row.status.class}`}>{row.status.text}</span>
                      </td>
                      <td>
                        {editingCell?.rowId === row.id && editingCell?.field === "remarks" ? (
                          <input
                            type="text"
                            className="table-edit-input"
                            value={row.remarks || ""}
                            onChange={(e) => handleCellEdit(row.id, "remarks", e.target.value)}
                            onBlur={handleCellBlur}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleCellBlur();
                            }}
                            autoFocus
                          />
                        ) : (
                          <span
                            onClick={() => handleCellFocus(row.id, "remarks")}
                            className="editable-cell"
                            title="Click to edit"
                          >
                            {row.remarks || "-"}
                          </span>
                        )}
                      </td>
                      <td>
                        <button className="remove-btn" onClick={() => removeRow(row.id)}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TrainingMatrix;

